import { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Users,
  UserPlus,
  Grid,
  List,
  Clock
} from 'lucide-react';
import { ResponsiveTable, StatusBadge } from '../../components/ui/table';
import { Button } from '../../components/ui/Button/Button';
import { Card } from '../../components/ui/Card';
import { UserCard, UserCardCompact } from '../../components/users/UserCard';
import { UserFilters } from '../../components/users/UserFilters';
import { UserDetailsModal } from '../../components/users/UserDetailsModal';
import { updateUser, deleteUser } from '../../store/userSlice';

// User role constants
export const USER_ROLES = {
  ADMIN: 'admin',
  MODERATOR: 'moderator',
  USER: 'user'
};

export function UserReportsPage() {
  const dispatch = useDispatch();
  const users = useSelector(state => state.users);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [genderFilter, setGenderFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // 'table', 'cards', 'compact'
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Filter and search users
  const filteredUsers = useMemo(() => {
    let filtered = users;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.address.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.user_role === roleFilter);
    }

    // Gender filter
    if (genderFilter !== 'all') {
      filtered = filtered.filter(user => user.gender.toLowerCase() === genderFilter.toLowerCase());
    }

    // Date range filter (based on date_of_birth for user reports)
    if (dateRange !== 'all') {
      const now = new Date();
      let startDate = new Date();

      switch (dateRange) {
        case 'recent':
          // Users registered in last 30 days (simulated)
          startDate.setDate(now.getDate() - 30);
          break;
        case 'month':
          startDate.setMonth(now.getMonth() - 1);
          break;
        case 'quarter':
          startDate.setMonth(now.getMonth() - 3);
          break;
        default:
          startDate = null;
      }

      if (startDate) {
        // For demo purposes, filter by birth date as we don't have registration date
        filtered = filtered.filter(user => new Date(user.date_of_birth) >= startDate);
      }
    }

    return [...filtered].sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [users, searchTerm, roleFilter, genderFilter, dateRange]);

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  const handleUpdateUser = (userId, updates) => {
    dispatch(updateUser({ id: userId, ...updates }));
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      dispatch(deleteUser(userId));
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setGenderFilter('all');
    setDateRange('all');
  };

  const handleAddUser = () => {
    // Navigate to add user page or open add user modal
    window.location.href = '/users/add';
  };

  const renderContent = () => {
    if (filteredUsers.length === 0) {
      return (
        <Card className="p-12 text-center">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
          <p className="text-gray-500">
            {searchTerm || roleFilter !== 'all' || genderFilter !== 'all' || dateRange !== 'all'
              ? 'Try adjusting your filters to see more users.'
              : 'User reports will appear here once users are registered.'
            }
          </p>
        </Card>
      );
    }

    switch (viewMode) {
      case 'cards':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredUsers.map((user) => (
              <UserCard
                key={user.id}
                user={user}
                onViewDetails={handleViewDetails}
                onUpdateUser={handleUpdateUser}
                onDeleteUser={handleDeleteUser}
              />
            ))}
          </div>
        );

      case 'compact':
        return (
          <Card className="divide-y divide-gray-200">
            {filteredUsers.map((user) => (
              <UserCardCompact
                key={user.id}
                user={user}
                onViewDetails={handleViewDetails}
              />
            ))}
          </Card>
        );

      default: // table
        const columns = [
          {
            key: 'id',
            header: 'User ID',
            primary: true,
            render: (value) => (
              <div className="font-medium text-gray-900">
                #{String(value).padStart(4, '0')}
              </div>
            )
          },
          {
            key: 'full_name',
            header: 'Full Name',
            render: (name, user) => (
              <div>
                <div className="font-medium text-gray-900">{name}</div>
                <div className="text-sm text-gray-500">@{user.username}</div>
              </div>
            )
          },
          {
            key: 'email',
            header: 'Contact',
            render: (email, user) => (
              <div>
                <div className="font-medium text-gray-900">{email}</div>
                <div className="text-sm text-gray-500">{user.phone_number}</div>
              </div>
            )
          },
          {
            key: 'user_role',
            header: 'Role',
            render: (role) => {
              const roleConfig = {
                [USER_ROLES.ADMIN]: { variant: 'success', label: 'Admin' },
                [USER_ROLES.MODERATOR]: { variant: 'warning', label: 'Moderator' },
                [USER_ROLES.USER]: { variant: 'default', label: 'User' }
              };

              const config = roleConfig[role] || { variant: 'default', label: role };
              return <StatusBadge status={config.label} variant={config.variant} />;
            }
          },
          {
            key: 'gender',
            header: 'Gender',
            render: (gender) => (
              <span className="text-sm text-gray-900">{gender}</span>
            )
          },
          {
            key: 'address',
            header: 'Location',
            render: (address) => (
              <div className="text-sm text-gray-900 max-w-xs truncate" title={address}>
                {address}
              </div>
            )
          },
          {
            key: 'date_of_birth',
            header: 'Birth Date',
            render: (date) => (
              <div className="text-sm">
                <div className="text-gray-900">
                  {new Date(date).toLocaleDateString()}
                </div>
                <div className="text-gray-500">
                  Age: {new Date().getFullYear() - new Date(date).getFullYear()}
                </div>
              </div>
            )
          }
        ];

        const actions = [
          {
            type: 'button',
            label: 'View Details',
            onClick: handleViewDetails,
            variant: 'primary'
          }
        ];

        return (
          <Card>
            <ResponsiveTable
              data={filteredUsers}
              columns={columns}
              actions={actions}
              emptyMessage="No users found matching your criteria"
            />
          </Card>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Reports</h1>
          <p className="mt-2 text-gray-600">
            Comprehensive user analytics and detailed user information reports
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'table' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
              }`}
              title="Table View"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('cards')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'cards' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
              }`}
              title="Card View"
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('compact')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'compact' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
              }`}
              title="Compact View"
            >
              <Clock className="w-4 h-4" />
            </button>
          </div>

          <Button variant="primary" onClick={handleAddUser}>
            <UserPlus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Filters */}
      <UserFilters
        searchTerm={searchTerm}
        roleFilter={roleFilter}
        genderFilter={genderFilter}
        dateRange={dateRange}
        onSearchChange={setSearchTerm}
        onRoleChange={setRoleFilter}
        onGenderChange={setGenderFilter}
        onDateRangeChange={setDateRange}
        onClearFilters={handleClearFilters}
        totalUsers={users.length}
        filteredUsers={filteredUsers.length}
      />

      {/* Content */}
      {renderContent()}

      {/* User Details Modal */}
      <UserDetailsModal
        user={selectedUser}
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedUser(null);
        }}
        onUpdateUser={handleUpdateUser}
        onDeleteUser={handleDeleteUser}
      />
    </div>
  );
}
